import * as React from "react";
import Modal from "./modal";
import { FormattedMessage, useIntl } from "react-intl";
import ReCAPTCHA from "react-google-recaptcha";
import Logo from "./logo";
import { GatsbyImage } from "gatsby-plugin-image";

const FormModal = ({ modal, isOpen, onClose, triggerRef }) => {
  const { formatMessage, locale } = useIntl();
  const [success, setSuccess] = React.useState(false);
  const [error, setError] = React.useState("");
  const [utmParams, setUtmParams] = React.useState(null);
  const formEl = React.useRef(null);
  const recaptchaEl = React.useRef(null);

  const handleSubmit = (event) => {
    event.preventDefault();
    recaptchaEl.current.execute();
  };

  const handleChange = async () => {
    setError("");

    const formData = new FormData(formEl.current);
    const listId = formData.get("listId");
    const locale = formData.get("locale");
    const firstName = formData.get("firstName");
    const lastName = formData.get("lastName");
    const phone = formData.get("phone");
    const email = formData.get("email");
    const utmCampaign = formData.get("utm_campaign");
    const utmId = formData.get("utm_id");
    const utmSource = formData.get("utm_source");
    const utmMedium = formData.get("utm_medium");
    const utmTerm = formData.get("utm_term");

    const response = await fetch("/api/newsletter-subscription", {
      method: "POST",
      headers: {
        Accept: "application/json",
        "Content-type": "application/json",
      },
      body: JSON.stringify({
        listId,
        locale,
        firstName,
        lastName,
        phone,
        email,
        utmCampaign,
        utmId,
        utmSource,
        utmMedium,
        utmTerm,
      }),
    });

    if (!response.ok) {
      const data = await response.json();
      setError(data.message);
      recaptchaEl.current.reset();
      return;
    }

    setSuccess(true);
    window.scrollTo(0, 0);

    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event: "FormSubmitOK",
      userEmail: email,
      userPhone: phone,
    });
  };

  React.useEffect(() => {
    if (typeof window !== "undefined") {
      setUtmParams(
        Object.keys(localStorage)
          .filter((key) => key.startsWith("utm_"))
          .map((key) => ({ key, value: localStorage.getItem(key) }))
      );
    }
  }, []);

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      triggerRef={triggerRef}
      variant="form"
    >
      <div className="modal-image">
        <Logo aria-hidden="true" />
        <GatsbyImage
          image={modal.image.gatsbyImageData}
          alt={modal.image.title}
        />
      </div>
      <div className="modal-form">
        <div className="modal-text">
          <h2 className="heading">
            {success ? modal.successTitle : modal.title}
          </h2>
          {success
            ? modal.successText && <p>{modal.successText.successText}</p>
            : modal.intro && <p>{modal.intro.intro}</p>}
          {success && modal.downloadLabel && modal.document && (
            <a
              className="button-link"
              href={modal.document.file.url}
              target="_blank"
            >
              {modal.downloadLabel}
            </a>
          )}
          {!success && (
            <form ref={formEl} onSubmit={handleSubmit}>
              <input type="hidden" name="locale" value={locale} />
              <input type="hidden" name="listId" value={32} />
              {utmParams &&
                utmParams.map(({ key, value }) => (
                  <input type="hidden" key={key} name={key} value={value} />
                ))}
              <label>
                <input
                  type="text"
                  name="firstName"
                  autoComplete="given-name"
                  placeholder={formatMessage({ id: "contactFirstName" })}
                  required
                />
                <span>
                  <FormattedMessage id="contactFirstName" /> *
                </span>
              </label>
              <label>
                <input
                  type="text"
                  name="lastName"
                  autoComplete="family-name"
                  placeholder={formatMessage({ id: "contactLastName" })}
                  required
                />
                <span>
                  <FormattedMessage id="contactLastName" /> *
                </span>
              </label>
              <label>
                <input
                  type="tel"
                  name="phone"
                  autoComplete="tel"
                  placeholder={formatMessage({ id: "contactPhone" })}
                  pattern="^\+\d{6,15}$"
                  title="+33600000000"
                />
                <span>
                  <FormattedMessage id="contactPhone" />
                </span>
              </label>
              <label>
                <input
                  type="email"
                  name="email"
                  autoComplete="email"
                  placeholder={formatMessage({ id: "contactEmail" })}
                  required
                />
                <span>
                  <FormattedMessage id="contactEmail" /> *
                </span>
              </label>
              <button type="submit" className="button-link">
                <FormattedMessage id="contactSend" />
              </button>
              <ReCAPTCHA
                ref={recaptchaEl}
                size="invisible"
                sitekey="6LcCWWgcAAAAAMX0Bfo5GDtTRuIyaLjDXcVSaBvX"
                onChange={handleChange}
              />
            </form>
          )}
          {error && <p>{error}</p>}
        </div>
      </div>
    </Modal>
  );
};

export default FormModal;
